// The round schedule: when each round starts, edited here and shown wherever a round is.
//
// Until now a tournament knew only its first and last day, so the app could not answer the
// single most common question a player asks — "when does round 3 start?" — and the TRF
// export wrote the start date into the round-date record for every round because it had
// nothing better. Both are fixed by storing one entry per round.
//
// The rules live in js/utils/round-schedule.js; this file reads inputs, writes inputs,
// and shows or hides things.

import { state, saveToLocalStorage, getActiveSettings, writeActiveSetting } from '../state.js';
import { escapeHtml } from '../utils/formatting.js';
import {
    normalizeSchedule,
    entryForRound,
    suggestSchedule
} from '../utils/round-schedule.js';

const modal = () => document.getElementById('round-schedule-modal');

// The editor's working copy, applied on Save. Same reason as the prize categories: an
// arbiter who opens the timetable to look at it must be able to close it unchanged.
let draft = [];

// WHICH group's timetable the editor is working on — the same arrangement as the prize
// categories (js/ui/prizes.js). Null is the Pairings page's own button, which edits the
// group that is open. A group id is the create/edit modal, where the group whose tab is
// showing is often not that one.
let editingGroupId = null;
let onEditorSaved = null;

/** How many rounds the editor offers — the configured count, never fewer than are played. */
function roundCount() {
    const settings = getActiveSettings();
    return Math.max(parseInt(settings.roundsCount, 10) || 0, state.rounds.length);
}

function groupById(id) {
    const groups = (state.activeTournament || {}).groups;
    if (!Array.isArray(groups)) return null;
    return groups.find(g => String(g.id) === String(id)) || null;
}

/**
 * Everything the editor needs about whatever it is editing: how many rows to show, how
 * many of those rounds have been played, the stored timetable, and the date and time
 * "Fill in" counts from.
 *
 * A group is not just a different `roundSchedule`. It has its own round count, its own
 * rounds — only the ACTIVE group's live in state.rounds — and, when `useGroupDates` is
 * set, its own start date. A section that plays on the Sunday must not be filled in from
 * the tournament's Saturday.
 */
function editingContext() {
    const t = state.activeTournament || {};

    if (!editingGroupId) {
        const settings = getActiveSettings();
        return {
            rows: roundCount(),
            playedRounds: state.rounds.length,
            schedule: settings.roundSchedule,
            startDate: t.startDate,
            startTime: t.startTime
        };
    }

    const group = groupById(editingGroupId) || {};
    const isOpenGroup = String(group.id) === String(state.activeGroupId);
    const played = isOpenGroup ? state.rounds.length : ((group.rounds || []).length);
    const ownDates = group.useGroupDates && group.startDate;
    return {
        rows: Math.max(parseInt(group.roundsCount, 10) || 0, played),
        playedRounds: played,
        schedule: group.roundSchedule,
        startDate: ownDates ? group.startDate : t.startDate,
        startTime: ownDates ? (group.startTime || t.startTime) : t.startTime
    };
}

/** The stored schedule, normalised to the current round count. */
export function currentSchedule() {
    return normalizeSchedule(getActiveSettings().roundSchedule, roundCount());
}

/**
 * "3 Oct 2026, 10:00" for one round, or '' when nothing is known about it.
 *
 * Dates go through window.trDate, the app's one place that turns an ISO date into
 * something a reader expects — Norwegian gets day-first, English keeps ISO on purpose.
 */
export function roundWhenLabel(roundNo) {
    const entry = entryForRound(currentSchedule(), roundNo);
    if (!entry.date && !entry.time) return '';
    const date = entry.date ? (window.trDate ? window.trDate(entry.date) : entry.date) : '';
    if (date && entry.time) return `${date}, ${entry.time}`;
    return date || entry.time;
}

// ---- the editor ------------------------------------------------------------

function renderDraft() {
    const body = document.getElementById('round-schedule-list');
    if (!body) return;

    const played = editingContext().playedRounds;
    body.innerHTML = draft.map((entry, i) => {
        const roundNo = i + 1;
        // A round already paired keeps its row and stays editable: a round that started an
        // hour late is a correction an arbiter makes afterwards, and the file that goes to
        // the federation should say when the game was actually played.
        const note = roundNo <= played
            ? `<span style="color: var(--text-muted); font-size: 0.75rem;">${tr('played')}</span>`
            : '';
        return `
            <tr data-index="${i}">
                <td style="width: 90px;"><strong>${tr('Round')} ${roundNo}</strong> ${note}</td>
                <td><input type="date" class="sched-field" data-field="date" value="${escapeHtml(entry.date)}" style="width: 100%;"></td>
                <td style="width: 130px;"><input type="time" class="sched-field" data-field="time" value="${escapeHtml(entry.time)}" style="width: 100%;"></td>
            </tr>`;
    }).join('');

    body.querySelectorAll('.sched-field').forEach(el => {
        el.addEventListener('change', () => {
            const index = Number(el.closest('tr').dataset.index);
            draft[index] = { ...draft[index], [el.dataset.field]: el.value };
            // Re-normalised straight away, so a browser that hands back an unexpected
            // format is caught here rather than in the file a week later.
            draft = normalizeSchedule(draft, draft.length);
        });
    });
}

function fillInSuggestion() {
    const ctx = editingContext();
    const perDay = parseInt(document.getElementById('schedule-rounds-per-day')?.value, 10) || 1;
    const gap = parseInt(document.getElementById('schedule-gap-minutes')?.value, 10) || 300;
    draft = suggestSchedule({
        startDate: ctx.startDate,
        startTime: ctx.startTime,
        roundCount: draft.length,
        roundsPerDay: perDay,
        gapMinutes: gap
    });
    renderDraft();
}

function clearSchedule() {
    draft = normalizeSchedule([], draft.length);
    renderDraft();
}

/**
 * @param {Object} [options]
 * @param {string} [options.groupId]  edit this group's timetable rather than the open
 *                                    group's — see editingGroupId
 * @param {Function} [options.onSaved] called after a successful save, for a caller that
 *                                     shows its own summary of it
 */
export function openScheduleEditor(options = {}) {
    if (state.isSpectatorMode || !state.activeTournament) return;
    editingGroupId = options.groupId || null;
    onEditorSaved = typeof options.onSaved === 'function' ? options.onSaved : null;

    const ctx = editingContext();
    draft = normalizeSchedule(ctx.schedule, ctx.rows);
    renderDraft();
    const m = modal();
    if (m) m.classList.add('active');
}

function saveSchedule(onSaved) {
    const kept = normalizeSchedule(draft, draft.length);
    const stored = editingGroupId
        ? (() => {
            const group = groupById(editingGroupId);
            if (!group) return false;
            group.roundSchedule = kept;
            return true;
        })()
        : writeActiveSetting('roundSchedule', kept);
    if (!stored) return;

    saveToLocalStorage();
    const m = modal();
    if (m) m.classList.remove('active');

    const notify = onEditorSaved;
    const editedAnotherGroup = !!editingGroupId;
    editingGroupId = null;
    onEditorSaved = null;

    // The Pairings page's own callback redraws the round titles, which describe the OPEN
    // group. Editing another group's timetable changes nothing there, so it is skipped
    // rather than redrawing the same thing.
    if (!editedAnotherGroup && typeof onSaved === 'function') onSaved();
    if (notify) notify();
}

/**
 * "3 of 7 rounds scheduled" — what the per-group settings show beside their button, so an
 * arbiter can see which sections have a timetable without opening each tab.
 */
export function scheduleSummaryText(group) {
    const g = group || {};
    // The open group's rounds live in state.rounds and its copy on the tournament object
    // can be behind; every other group carries its own. Same rule as editingContext().
    const played = String(g.id) === String(state.activeGroupId)
        ? state.rounds.length
        : ((g.rounds || []).length);
    const total = Math.max(parseInt(g.roundsCount, 10) || 0, played);
    const scheduled = normalizeSchedule(g.roundSchedule, total).filter(e => e.date).length;
    if (scheduled === 0) return tr('No schedule');
    return tr('{scheduled} of {total} rounds scheduled', { scheduled, total });
}

// ---- wiring ----------------------------------------------------------------

/** Show the editor button only to an arbiter with a tournament open that has rounds. */
export function syncScheduleButtons() {
    const edit = document.getElementById('btn-round-schedule');
    if (edit) {
        edit.style.display = (!state.isSpectatorMode && state.activeTournament && roundCount() > 0) ? '' : 'none';
    }
}

export function setupSchedule(deps = {}) {
    // Called with no arguments on purpose: a listener passed straight through would hand
    // the click Event in as the options object.
    const open = document.getElementById('btn-round-schedule');
    if (open) open.addEventListener('click', () => openScheduleEditor());

    // Cancelling leaves no target behind — see the same reset in js/ui/prizes.js.
    const m = modal();
    if (m) {
        m.querySelectorAll('.btn-close-modal').forEach(btn => {
            btn.addEventListener('click', () => { editingGroupId = null; onEditorSaved = null; });
        });
    }

    const fill = document.getElementById('btn-schedule-fill');
    if (fill) fill.addEventListener('click', fillInSuggestion);

    const clear = document.getElementById('btn-schedule-clear');
    if (clear) clear.addEventListener('click', clearSchedule);

    const save = document.getElementById('btn-save-schedule');
    if (save) save.addEventListener('click', () => saveSchedule(deps.onSaved));

    syncScheduleButtons();
}
